import { useCallback, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import {
  getCachedData,
  setCachedData,
  getFetchPromise,
  setFetchPromise,
} from './CacheUtility';

const debounceDelay = 300;

const debouncePromise = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => Promise<ReturnType<T>>) => {
  const debounced = debounce(
    (
      resolve: (value: ReturnType<T>) => void,
      reject: (reason?: any) => void,
      ...args: Parameters<T>
    ) => {
      fn(...args)
        .then(resolve)
        .catch(reject);
    },
    delay
  );

  return (...args: Parameters<T>) =>
    new Promise<ReturnType<T>>((resolve, reject) => {
      debounced(resolve, reject, ...args);
    });
};

const useCachedList = <T extends unknown>(
  cacheKey: string,
  useLazyFetchHook: () => {
    execute: () => Promise<{ items: T[] } | undefined>;
  },
  ttlMinutes: number = 10
) => {
  const { execute } = useLazyFetchHook();
  const debouncedExecuteRef = useRef(debouncePromise(execute, debounceDelay));

  useEffect(() => {
    debouncedExecuteRef.current = debouncePromise(execute, debounceDelay);
  }, [execute]);

  const getList = useCallback(
    async (id?: string, refresh?: boolean): Promise<T[]> => {
      const refreshCache = async () => {
        try {
          const result = await debouncedExecuteRef.current();
          if (!result || !result.items) {
            return [];
          }

          const data = result.items;
          setCachedData(cacheKey, data, ttlMinutes);

          return data;
        } catch (error) {
          return [];
        }
      };

      const cachedItem = getCachedData<T>(cacheKey);

      if (cachedItem && !refresh) {
        if (id) {
          const foundItem = cachedItem.data.find((d: any) => d.id === id);
          if (foundItem) {
            return [foundItem];
          } else {
            return await refreshCache();
          }
        }

        return Promise.resolve(cachedItem.data);
      } else {
        let fetchPromise = getFetchPromise(cacheKey);
        if (!fetchPromise) {
          fetchPromise = refreshCache();
          setFetchPromise(cacheKey, fetchPromise);
        }
        const data = await fetchPromise;
        return data;
      }
    },
    [cacheKey, ttlMinutes]
  );

  return {
    getList,
  };
};

export default useCachedList;
